const ABSOLUTE_URL_SCHEME = /^[a-z][a-z\d+\-.]*:/i;

const parseOrigin = (value?: string | null) => {
  if (!value) {
    return null;
  }

  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
};

const normalizeCallbackInput = (
  value?: string | null,
  allowRelativePathWithoutSlash = false
) => {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return '';
  }

  // Preserve compatibility with legacy encoded quote-prefixed callback values.
  const withoutLeadingQuote =
    trimmed.startsWith('"/') || trimmed.startsWith("'/")
      ? trimmed.slice(1)
      : trimmed;

  if (
    allowRelativePathWithoutSlash &&
    !withoutLeadingQuote.startsWith('/') &&
    !ABSOLUTE_URL_SCHEME.test(withoutLeadingQuote)
  ) {
    return `/${withoutLeadingQuote}`;
  }

  return withoutLeadingQuote;
};

type SanitizeRedirectUrlOptions = {
  url?: string | null;
  baseUrl: string;
  fallbackUrl?: string;
  allowedOrigins?: Array<string | null | undefined>;
  allowRelativePathWithoutSlash?: boolean;
};

export const sanitizeRedirectUrl = ({
  url,
  baseUrl,
  fallbackUrl = baseUrl,
  allowedOrigins = [],
  allowRelativePathWithoutSlash = false,
}: SanitizeRedirectUrlOptions) => {
  const baseOrigin = parseOrigin(baseUrl);
  const base = baseOrigin ? new URL(baseUrl) : null;
  const normalizedUrl = normalizeCallbackInput(
    url,
    allowRelativePathWithoutSlash
  );

  if (!normalizedUrl || !base) {
    return fallbackUrl;
  }

  if (normalizedUrl.startsWith('/') && !normalizedUrl.startsWith('//')) {
    return new URL(normalizedUrl, base).toString();
  }

  const allowedOriginSet = new Set(
    [baseUrl, ...allowedOrigins]
      .map(parseOrigin)
      .filter((origin): origin is string => Boolean(origin))
  );

  try {
    const candidate = new URL(normalizedUrl);
    if (allowedOriginSet.has(candidate.origin)) {
      return candidate.toString();
    }
  } catch {
    return fallbackUrl;
  }

  return fallbackUrl;
};
